/**
 * F2 metadata service context.
 *
 * VITE_METADATA_SERVICE controls the adapter:
 *   mock (default) — MockMetadataService; full compare screen with no orgs
 *   http           — HttpMetadataService; calls /api/metadata/*
 */

import { createContext, useContext, type ReactNode } from 'react'
import type { IMetadataService } from './IMetadataService.interface'
import { MockMetadataService }   from './MockMetadataService'
import { HttpMetadataService }   from './HttpMetadataService'

// ── Service resolution ────────────────────────────────────────────────────────

function resolveService(): IMetadataService {
  const key = (import.meta.env.VITE_METADATA_SERVICE as string | undefined) ?? 'mock'
  switch (key) {
    case 'http': return new HttpMetadataService()
    case 'mock':
    default:     return new MockMetadataService()
  }
}

const defaultService = resolveService()

// ── Context ───────────────────────────────────────────────────────────────────

const MetadataServiceContext = createContext<IMetadataService>(defaultService)

interface MetadataServiceProviderProps {
  children: ReactNode
  /** Override the service instance — used in tests. */
  service?: IMetadataService
}

export function MetadataServiceProvider({
  children,
  service: override,
}: MetadataServiceProviderProps) {
  return (
    <MetadataServiceContext.Provider value={override ?? defaultService}>
      {children}
    </MetadataServiceContext.Provider>
  )
}

export function useMetadataService(): IMetadataService {
  return useContext(MetadataServiceContext)
}
